/**
 * Single chat restore path.
 *
 * One GET → one paint: conversation text, tool bars, intent/plan cards, open HITL,
 * Live SSE paints cards via chat-helpers (append/update in place). Restore does not
 * reshuffle transcripts.
 */
import type { MutableRefObject } from "react";

import {
  displayChatTitle,
  listChatHistory,
  awaitPersistedLiveBuffer,
  readPersistedLiveBuffer,
  releaseIdleDiskCache,
  setChatLive,
} from "@/features/chat/multi/history/chat-history";
import { getLiveBuffer, setLiveBuffer } from "@/features/chat/multi/store/chat-live-store";
import {
  isChatStreaming,
  setChatAwaitingInput,
  setChatBuilding,
  setChatStreaming,
  setFocusedSession,
} from "@/features/chat/multi/signals/chat-signals";
import { getSavedFlow } from "@/features/flow-builder/lib/flow-utils";
import {
  abortActiveWorkflowStream,
  emitChatLoaded,
  isWorkflowStreamActive,
  reconcileSessionHitl,
  syncFlowBuilderSessionUrl,
  type SessionHitlSnapshot,
} from "@/shared/builder-session";
import { sanitizePublicTranscript } from "@/features/chat/multi/contract/message-contract";
import {
  asStructuredIntent,
  asWorkflowPlan,
  composioDecisionMessageId,
  isDraftSavedMessage,
  mergePlanForDisplay,
  paintDraftSavedCard,
  rejectsAwaitingIntentDowngrade,
} from "@/features/chat/builder/sse/chat-helpers";
import { stampConfirmedIntentCards, composioApprovalIdFromMessage } from "@/shared/builder-session";
import { persistLastFlowBuilderSessionId } from "@/features/chat/multi/persistence/clear-persisted-session";
import { ensureSessionHitlFromServer } from "@/features/chat/builder/sync/sync-session-ui";
import { shouldAutoResumeAfterRestore } from "@/features/chat/builder/sync/resume-autonomous-build";
import { resumeBuildAfterRestore } from "@/features/chat/multi/transport/multiplex-stream";
import type {
  BuilderContextValue,
  BuilderMessage,
  PendingComposioExec,
  StructuredIntent,
  TestRunSummary,
  WorkflowInputField,
  WorkflowPlan,
} from "@/features/chat/builder/builder-types";
import { flowopsFetch } from "@/shared/builder-session/flowops-api-url";

type BuilderRef = MutableRefObject<BuilderContextValue>;

const SESSION_GONE_MESSAGE =
  "This build session no longer exists on the agent service. Start a new build.";

export type RecalledTimelineMessage = {
  id?: string;
  type: "user" | "assistant";
  text?: string;
  content?: string;
  details?: unknown;
};

export type LiveSessionSnapshot = SessionHitlSnapshot & {
  id?: string;
  prompt?: string;
  workflowName?: string;
  plan?: WorkflowPlan | null;
  intent?: StructuredIntent | null;
  nodes?: unknown[];
  edges?: unknown[];
  workflowId?: string;
  inputs?: WorkflowInputField[];
  collectedInputs?: Record<string, unknown>;
  messages?: BuilderMessage[];
  recalledMessages?: RecalledTimelineMessage[];
  approvedAt?: string;
  /** Client + list payloads. */
  runActive?: boolean;
  /** GET /api/workflow/session returns this (FlowOps peekFlowopsRunLive). */
  isRunning?: boolean;
  durableRunId?: string | null;
  parkedForInput?: boolean;
  lastTestRun?: TestRunSummary | null;
  testRunStale?: boolean;
  lastFinalizeError?: string | null;
};

/** FlowOps session GET uses `isRunning`; list/sidebar may send `runActive`. */
export function snapshotRunActive(snapshot: { runActive?: boolean; isRunning?: boolean }): boolean {
  return Boolean(snapshot.runActive || snapshot.isRunning);
}

export function isSessionGoneError(message: string | null | undefined): boolean {
  const text = message?.trim() ?? "";
  return (
    /session not found or expired/i.test(text) ||
    /live session expired/i.test(text) ||
    /no longer exists on the agent service/i.test(text)
  );
}

export function isUsableSessionSnapshot(
  snapshot: LiveSessionSnapshot | null | undefined,
): snapshot is LiveSessionSnapshot & { id: string } {
  return Boolean(snapshot?.id?.trim());
}

export function isSessionFetchAbort(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === "AbortError"
    : error instanceof Error && error.name === "AbortError";
}

export async function fetchSessionSnapshot(
  sessionId: string,
  timeoutMs = 45_000,
): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await flowopsFetch(`/api/workflow/session/${encodeURIComponent(sessionId)}`, {
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timer);
  }
}

export function markClientSessionGone(builder: BuilderContextValue, sessionId: string): void {
  const id = sessionId.trim();
  if (!id) return;
  if (isWorkflowStreamActive(id)) abortActiveWorkflowStream(id);
  builder.setPendingUserQuestion(null);
  builder.clearPendingComposioExec();
  builder.setPendingRuntimeInput(null);
  builder.setDurableRunId(null);
  builder.setBusy(false);
  builder.setBuildStalled(false);
  builder.setPhase("idle");
  builder.setError(SESSION_GONE_MESSAGE);
  builder.setSessionId(id);
  if (builder.name === "Untitled Workflow") {
    const item = listChatHistory().find((chat) => chat.id === id);
    if (item) builder.setName(displayChatTitle(item));
  }
  setChatLive(id, false);
  setChatAwaitingInput(id, false);
}

function hasUserTurn(messages: BuilderMessage[] | undefined): boolean {
  return Boolean(
    messages?.some(
      (message) =>
        message.type === "user" && typeof message.text === "string" && message.text.trim(),
    ),
  );
}

function cleanTranscript(messages: BuilderMessage[]): BuilderMessage[] {
  return sanitizePublicTranscript(messages);
}

function seedUser(sessionId: string, seedPrompt: string): BuilderMessage[] {
  return seedPrompt ? [{ id: `message-restore-${sessionId}`, type: "user", text: seedPrompt }] : [];
}

function transcriptWeight(messages: BuilderMessage[]): number {
  let weight = messages.length;
  for (const message of messages) {
    if (message.type === "tool" || message.type === "intent" || message.type === "plan") {
      weight += 2;
    }
  }
  return weight;
}

/** Prefer complete uiMessages; otherwise Mastra recall text. */
function messagesFromSnapshot(snapshot: LiveSessionSnapshot): BuilderMessage[] {
  const sessionId = snapshot.id?.trim() || "session";

  if (hasUserTurn(snapshot.messages) && Array.isArray(snapshot.messages)) {
    return snapshot.messages
      .filter((message): message is BuilderMessage => Boolean(message?.type))
      .map((message, index) => ({
        ...message,
        id: message.id?.trim() || `ui-${sessionId}-${index}`,
      }))
      .filter((message) => {
        if (message.type === "user" || message.type === "assistant") {
          return typeof message.text === "string" && message.text.trim().length > 0;
        }
        return (
          message.type === "intent" ||
          message.type === "plan" ||
          message.type === "tool" ||
          message.type === "status" ||
          message.type === "error"
        );
      });
  }

  const recalled = snapshot.recalledMessages;
  if (Array.isArray(recalled) && recalled.length > 0) {
    return recalled.flatMap((entry, index) => {
      const text = (entry.text ?? entry.content ?? "").trim();
      if (!text || (entry.type !== "user" && entry.type !== "assistant")) return [];
      return [
        {
          id: entry.id?.trim() || `memory-${sessionId}-${index}`,
          type: entry.type,
          text,
          ...(entry.details !== undefined ? { details: entry.details } : {}),
        } satisfies BuilderMessage,
      ];
    });
  }

  if (Array.isArray(snapshot.messages) && snapshot.messages.length > 0) {
    return snapshot.messages
      .filter((message): message is BuilderMessage => Boolean(message?.type))
      .map((message, index) => ({
        ...message,
        id: message.id?.trim() || `ui-${sessionId}-${index}`,
      }));
  }

  return [];
}

function normalizeComposioDecisions(messages: BuilderMessage[]): BuilderMessage[] {
  const seen = new Set<string>();
  const out: BuilderMessage[] = [];
  for (const message of messages) {
    const approvalId = composioApprovalIdFromMessage(message);
    if (!approvalId) {
      out.push(message);
      continue;
    }
    if (seen.has(approvalId)) continue;
    seen.add(approvalId);
    const details = message.details as { kind?: string; pending?: PendingComposioExec };
    out.push({
      ...message,
      id: composioDecisionMessageId(approvalId),
      details: {
        ...details,
        pending: { ...details.pending!, approvalId },
      },
    });
  }
  return out;
}

function insertAfterFirstUser(messages: BuilderMessage[], card: BuilderMessage): BuilderMessage[] {
  const firstUser = messages.findIndex((message) => message.type === "user");
  const next = [...messages];
  next.splice(firstUser < 0 ? next.length : firstUser + 1, 0, card);
  return next;
}

/**
 * When durable uiMessages are missing, place intent/plan once from session fields.
 * Never used when uiMessages already has a user turn (complete painted timeline).
 */
function fillMissingReviewCards(
  messages: BuilderMessage[],
  snapshot: LiveSessionSnapshot,
): BuilderMessage[] {
  const sessionId = snapshot.id?.trim() || "session";
  let next = normalizeComposioDecisions(messages);
  const openQuestions = Boolean(snapshot.pendingUserQuestion?.questions?.length);

  const intent = asStructuredIntent(snapshot.intent);
  if (
    intent &&
    !next.some((message) => message.type === "intent") &&
    intent.intent.unknowns.length === 0 &&
    !openQuestions &&
    (snapshot.status === "awaiting_intent_approval" ||
      intent.status === "confirmed" ||
      intent.status === "awaiting_confirmation")
  ) {
    next = insertAfterFirstUser(next, {
      id: `restore-intent-${sessionId}`,
      type: "intent",
      text: "Intent ready for approval.",
      details: intent,
    });
  }

  const plan = asWorkflowPlan(snapshot.plan) ?? snapshot.plan ?? null;
  const canProjectPlan =
    plan &&
    Array.isArray(plan.steps) &&
    plan.steps.length > 0 &&
    !next.some((message) => message.type === "plan") &&
    (snapshot.status === "awaiting_approval" ||
      Boolean(snapshot.approvedAt) ||
      (Array.isArray(snapshot.nodes) && snapshot.nodes.length > 0) ||
      snapshot.status === "building" ||
      snapshot.status === "testing" ||
      snapshot.status === "complete");

  if (canProjectPlan && plan) {
    const intentIdx = next.findIndex((message) => message.type === "intent");
    const card: BuilderMessage = {
      id: `restore-plan-${sessionId}`,
      type: "plan",
      text: "Plan ready for approval.",
      details: plan,
    };
    if (intentIdx >= 0) {
      const copy = [...next];
      copy.splice(intentIdx + 1, 0, card);
      next = copy;
    } else {
      next = insertAfterFirstUser(next, card);
    }
  }

  return next;
}

/** FlowOps showTestActions: testing/complete always exposes Validate — invent the card if SSE was missed. */
function ensureDraftSavedMessage(
  messages: BuilderMessage[],
  snapshot: LiveSessionSnapshot,
): BuilderMessage[] {
  const status = String(snapshot.status ?? "");
  if (status !== "testing" && status !== "complete") return messages;
  if (messages.some(isDraftSavedMessage)) return messages;
  const sessionId = snapshot.id?.trim() || "session";
  return [
    ...messages,
    {
      id: `restore-draft-saved-${sessionId}`,
      type: "status",
      text: "Draft saved",
      details: { kind: "draft-saved" },
    },
  ];
}

function buildRestoreMessages(options: {
  snapshot: LiveSessionSnapshot;
  seedPrompt: string;
  streamActive: boolean;
}): BuilderMessage[] {
  const { snapshot, seedPrompt, streamActive } = options;
  const sessionId = snapshot.id!.trim();
  const durableUi = hasUserTurn(snapshot.messages);
  const server = cleanTranscript(messagesFromSnapshot(snapshot));
  const memoryBuffer = getLiveBuffer(sessionId);
  const live = cleanTranscript(
    memoryBuffer.length ? memoryBuffer : readPersistedLiveBuffer(sessionId),
  );

  // FlowOps IndexedDB parity: local painted timeline wins over Memory recall.
  // Prefer server only when it is strictly richer (e.g. reopen with newer ui).
  // Live stream: always keep the in-flight buffer (no server merge).
  let usedLocal = false;
  let messages: BuilderMessage[];
  if (streamActive && live.length > 0) {
    messages = live;
    usedLocal = true;
  } else if (live.length > 0 && hasUserTurn(live)) {
    if (!hasUserTurn(server) || transcriptWeight(live) >= transcriptWeight(server)) {
      messages = live;
      usedLocal = true;
    } else {
      messages = server;
    }
  } else if (server.length > 0) {
    messages = server;
  } else if (live.length > 0) {
    messages = live;
    usedLocal = true;
  } else {
    messages = seedUser(sessionId, seedPrompt);
  }

  if (!hasUserTurn(messages)) {
    messages = [...seedUser(sessionId, seedPrompt), ...messages];
  }
  // Only invent review cards when we have no local/ui timeline (Memory-only).
  if (!durableUi && !usedLocal) {
    messages = fillMissingReviewCards(messages, snapshot);
  }
  const withIntent = stampConfirmedIntentCards(messages, asStructuredIntent(snapshot.intent));
  const withPlan = stampPlanCardFromSession(
    withIntent,
    asWorkflowPlan(snapshot.plan) ?? snapshot.plan,
  );
  // Always — local buffer often predates draft-saved SSE, so Validate would never appear.
  return ensureDraftSavedMessage(withPlan, snapshot);
}

/** Fill plan.summary / steps on transcript plan rows from session.plan SoT. */
function stampPlanCardFromSession(
  messages: BuilderMessage[],
  sessionPlan: WorkflowPlan | null | undefined,
): BuilderMessage[] {
  if (!sessionPlan) return messages;
  let changed = false;
  const next = messages.map((message) => {
    if (message.type !== "plan") return message;
    const merged = mergePlanForDisplay(message.details as WorkflowPlan | undefined, sessionPlan);
    if (!merged) return message;
    const prior = message.details as WorkflowPlan | undefined;
    if (
      (prior?.summary?.trim() || "") === (merged.summary?.trim() || "") &&
      (prior?.steps?.length ?? 0) === (merged.steps?.length ?? 0)
    ) {
      return message;
    }
    changed = true;
    return { ...message, details: merged, text: message.text || "Plan ready for approval." };
  });
  return changed ? next : messages;
}

function snapshotHasPendingHitl(snapshot: LiveSessionSnapshot): boolean {
  return Boolean(
    snapshot.parkedForInput ||
    snapshot.pendingUserQuestion ||
    (Array.isArray(snapshot.pendingComposioExecApprovals) &&
      snapshot.pendingComposioExecApprovals.length > 0) ||
    (Array.isArray(snapshot.pendingRuntimeInputRequests) &&
      snapshot.pendingRuntimeInputRequests.length > 0) ||
    snapshot.pauseReason?.fields?.length ||
    snapshot.status === "awaiting_approval" ||
    snapshot.status === "awaiting_intent_approval" ||
    snapshot.status === "paused",
  );
}

/**
 * FlowOps `applyBuildSessionSnapshot(snapshot, keepChat)` — reconnect / gap catch-up.
 * Keeps the live transcript; rebuilds graph, HITL, phase, and busy from the server document.
 */
export function applySessionResync(options: {
  snapshot: LiveSessionSnapshot;
  builder: BuilderContextValue;
}): {
  runActive: boolean;
  shouldResume: boolean;
  hasPendingHitl: boolean;
} {
  const empty = { runActive: false, shouldResume: false, hasPendingHitl: false };
  const { snapshot, builder } = options;
  if (!isUsableSessionSnapshot(snapshot)) return empty;
  if (builder.sessionId && builder.sessionId !== snapshot.id) return empty;

  // Client already mid-turn (mutation returned, hub still catching up). A lagging
  // snapshot often has isRunning=false for a few seconds — do not flash idle.
  // Do NOT use isChatBuilding here: that flag is derived from busy and would latch
  // Working… / lock Deploy after Edit Workflow → leave → reopen.
  const streamLive = isWorkflowStreamActive(snapshot.id) || isChatStreaming(snapshot.id);
  const clientLive = builder.busy || streamLive;

  // FlowOps live-only flags must stay cleared after reconnect catch-up.
  builder.setError(null);
  builder.setBuildStalled(false);
  builder.setIsStoppingAgent(false);
  builder.setExecutingNodeId(null);
  builder.endProbe();
  // Only drop Working… when we are not already in a live client turn.
  if (!clientLive) builder.setBusy(false);

  if (snapshot.workflowName?.trim()) {
    const chat = listChatHistory().find((item) => item.id === snapshot.id);
    if (!chat?.titleLocked) builder.setName(snapshot.workflowName.trim());
  }
  if (snapshot.workflowId) builder.setWorkflowId(snapshot.workflowId);
  if (typeof snapshot.prompt === "string" && snapshot.prompt.trim()) {
    builder.setSourcePrompt(snapshot.prompt.trim());
  }

  const keepApprovedIntent =
    builder.intent?.status === "confirmed" && builder.sessionId === snapshot.id;
  const intent = asStructuredIntent(snapshot.intent);
  if (intent) {
    if (!rejectsAwaitingIntentDowngrade(builder.intent, intent)) {
      builder.setIntent(intent);
    }
  } else if (snapshot.intent && !keepApprovedIntent) {
    builder.setIntent(snapshot.intent);
  }

  const plan = asWorkflowPlan(snapshot.plan) ?? snapshot.plan ?? null;
  if (plan) builder.setPlan(plan as WorkflowPlan);

  if (snapshot.inputs?.length) builder.setWorkflowInputs(snapshot.inputs);
  builder.syncCollectedInputs(
    snapshot.collectedInputs ?? {},
    (snapshot.inputs ?? []).map((field) => field.name),
  );

  if (typeof snapshot.durableRunId === "string" && snapshot.durableRunId.trim()) {
    builder.setDurableRunId(snapshot.durableRunId.trim());
  } else if (snapshot.durableRunId === null) {
    builder.setDurableRunId(null);
  }

  if (Array.isArray(snapshot.nodes) && snapshot.nodes.length) {
    builder.applyGraphEvent("graph-relayout", { nodes: snapshot.nodes });
  }
  if (Array.isArray(snapshot.edges) && snapshot.edges.length) {
    builder.applyGraphEvent("edges-sync", {
      edges: snapshot.edges,
      nodes: snapshot.nodes,
    });
  }

  if (snapshot.lastTestRun) builder.setLastTestRun(snapshot.lastTestRun);
  // FlowOps applyBuildSessionSnapshot — always assign (null clears a prior failure).
  builder.setLastFinalizeError(
    typeof snapshot.lastFinalizeError === "string"
      ? snapshot.lastFinalizeError.trim() || null
      : null,
  );
  if (typeof snapshot.testRunStale === "boolean") {
    builder.setTestRunStale(snapshot.testRunStale);
  }

  reconcileSessionHitl(builder, snapshot.id, snapshot);

  const snapshotHasNodes =
    (Array.isArray(snapshot.nodes) && snapshot.nodes.length > 0) || builder.nodes.length > 0;
  const intentApprovalPark = Boolean(
    !keepApprovedIntent &&
    intent &&
    intent.status !== "confirmed" &&
    intent.intent.unknowns.length === 0 &&
    (snapshot.status === "awaiting_intent_approval" ||
      builder.messages.some((message) => message.type === "intent")),
  );

  const status = String(snapshot.status ?? "");
  if (status === "complete") {
    builder.setPhase("complete");
    builder.markFinalized();
    paintDraftSavedCard(builder);
  } else if (status === "awaiting_approval") {
    builder.setPhase(snapshotHasNodes ? "building" : "awaiting approval");
  } else if (intentApprovalPark || status === "awaiting_intent_approval") {
    builder.setPhase(keepApprovedIntent ? "planning" : "awaiting intent approval");
  } else if (status === "testing" || status === "building" || status === "planning") {
    builder.setPhase(status);
  } else if (
    builder.pendingUserQuestion ||
    builder.pendingComposioExecQueue.length > 0 ||
    builder.pendingRuntimeInput
  ) {
    builder.setPhase("awaiting input");
  }

  const hasPendingHitl = Boolean(
    builder.pendingUserQuestion ||
    builder.pendingComposioExecQueue.length > 0 ||
    builder.pendingRuntimeInput ||
    intentApprovalPark ||
    (status === "awaiting_approval" && snapshot.plan && !snapshotHasNodes) ||
    (status === "awaiting_intent_approval" && snapshot.intent && !keepApprovedIntent),
  );
  setChatAwaitingInput(snapshot.id, hasPendingHitl);

  const runActive = snapshotRunActive(snapshot) && status !== "complete";
  const shouldResume =
    status !== "complete" &&
    !clientLive &&
    !runActive &&
    !isWorkflowStreamActive(snapshot.id) &&
    shouldAutoResumeAfterRestore(status, hasPendingHitl);

  // FlowOps: re-arm for live run / deferred orphan resume.
  // Preserve client live across lagging snapshots (brand-new turn attach race).
  // Complete sessions must stay idle — stale multiplex live flags must not lock Deploy.
  if (status === "complete") {
    builder.setBusy(false);
    setChatLive(snapshot.id, false);
    setChatStreaming(snapshot.id, false);
    setChatBuilding(snapshot.id, false);
  } else if (hasPendingHitl) {
    builder.setBusy(false);
    setChatLive(snapshot.id, false);
  } else if (runActive || shouldResume || clientLive) {
    builder.setBusy(true);
    builder.setBuildStalled(false);
    setChatLive(snapshot.id, true);
  } else {
    builder.setBusy(false);
    setChatLive(snapshot.id, false);
    setChatStreaming(snapshot.id, false);
    setChatBuilding(snapshot.id, false);
    if (status === "testing" || status === "complete") {
      paintDraftSavedCard(builder);
    }
  }

  return { runActive: runActive || clientLive, shouldResume, hasPendingHitl };
}

/**
 * Apply a session snapshot in one shot: transcript + HITL + graph + phase.
 * This is the only restore entry point.
 */
export function applySessionRestore(options: {
  snapshot: LiveSessionSnapshot;
  builderRef: BuilderRef;
  normalizedPrompt?: string;
}): void {
  const { snapshot, builderRef } = options;
  const normalizedPrompt = options.normalizedPrompt?.trim() ?? "";
  if (!isUsableSessionSnapshot(snapshot)) return;

  const activeBuilder = builderRef.current;
  const snapshotPrompt = snapshot.prompt?.trim() ?? "";
  const softParked = snapshotHasPendingHitl(snapshot);
  const runActive = snapshotRunActive(snapshot);
  const streamActive = !softParked && (runActive || isWorkflowStreamActive(snapshot.id));
  const seedPrompt = snapshotPrompt || normalizedPrompt || "";
  const messages = buildRestoreMessages({ snapshot, seedPrompt, streamActive });

  const intentForPark = asStructuredIntent(snapshot.intent);
  const keepApprovedIntent =
    Boolean(activeBuilder.sessionId) &&
    activeBuilder.sessionId === snapshot.id &&
    activeBuilder.intent?.status === "confirmed";
  const intentApprovalPark = Boolean(
    !keepApprovedIntent &&
    intentForPark &&
    intentForPark.status !== "confirmed" &&
    intentForPark.intent.unknowns.length === 0 &&
    !snapshot.pendingUserQuestion?.questions?.length &&
    (snapshot.status === "awaiting_intent_approval" ||
      messages.some((message) => message.type === "intent")),
  );

  const snapshotHasNodes = Array.isArray(snapshot.nodes) && snapshot.nodes.length > 0;
  // Server can still say awaiting_approval after Build drew nodes — don't re-park.
  const restorePhase =
    softParked &&
    snapshot.status !== "awaiting_approval" &&
    snapshot.status !== "awaiting_intent_approval"
      ? "awaiting input"
      : snapshot.status === "awaiting_approval"
        ? snapshotHasNodes
          ? "building"
          : "awaiting approval"
        : intentApprovalPark ||
            (snapshot.status === "awaiting_intent_approval" && !keepApprovedIntent)
          ? "awaiting intent approval"
          : (snapshot.status ?? "planning");

  if (softParked) {
    setChatLive(snapshot.id, false);
  }

  activeBuilder.hydrateChatSession({
    sessionId: snapshot.id,
    messages,
    name: snapshot.workflowName,
    workflowId: snapshot.workflowId,
    sourcePrompt: snapshotPrompt || normalizedPrompt || null,
    phase: restorePhase,
  });
  if (softParked) activeBuilder.setBusy(false);

  const intent = asStructuredIntent(snapshot.intent);
  if (intent) {
    if (!rejectsAwaitingIntentDowngrade(activeBuilder.intent, intent)) {
      activeBuilder.setIntent(intent);
      if (
        intentApprovalPark &&
        messages.some((message) => message.type === "intent") &&
        intent.status !== "confirmed"
      ) {
        activeBuilder.setPhase("awaiting intent approval");
        activeBuilder.setBusy(false);
      }
    }
  } else if (snapshot.intent && !keepApprovedIntent) {
    activeBuilder.setIntent(snapshot.intent);
  }

  const plan = asWorkflowPlan(snapshot.plan) ?? snapshot.plan ?? null;
  if (plan) activeBuilder.setPlan(plan as WorkflowPlan);

  setLiveBuffer(snapshot.id, activeBuilder.messages);
  if (!streamActive && hasUserTurn(snapshot.messages)) {
    releaseIdleDiskCache(snapshot.id);
  }

  if (snapshot.inputs?.length) activeBuilder.setWorkflowInputs(snapshot.inputs);
  activeBuilder.syncCollectedInputs(
    snapshot.collectedInputs ?? {},
    (snapshot.inputs ?? []).map((field) => field.name),
  );

  if (typeof snapshot.durableRunId === "string" && snapshot.durableRunId.trim()) {
    activeBuilder.setDurableRunId(snapshot.durableRunId.trim());
  } else if (snapshot.durableRunId === null) {
    activeBuilder.setDurableRunId(null);
  }

  if (Array.isArray(snapshot.nodes) && snapshot.nodes.length) {
    activeBuilder.applyGraphEvent("graph-relayout", { nodes: snapshot.nodes });
    if (activeBuilder.phase === "awaiting approval" || snapshot.status === "awaiting_approval") {
      activeBuilder.setPhase(
        snapshot.status === "testing" || snapshot.status === "complete"
          ? snapshot.status
          : "building",
      );
    }
  }
  if (Array.isArray(snapshot.edges) && snapshot.edges.length) {
    activeBuilder.applyGraphEvent("edges-sync", {
      edges: snapshot.edges,
      nodes: snapshot.nodes,
    });
  }

  // After graph paint — FlowOps applyBuildSessionSnapshot test-run fields.
  if (snapshot.lastTestRun) {
    activeBuilder.setLastTestRun(snapshot.lastTestRun);
  }
  activeBuilder.setLastFinalizeError(
    typeof snapshot.lastFinalizeError === "string"
      ? snapshot.lastFinalizeError.trim() || null
      : null,
  );
  if (snapshot.testRunStale) {
    activeBuilder.setTestRunStale(true);
  }

  if (snapshot.workflowId) {
    void (async () => {
      try {
        const flow = await getSavedFlow(snapshot.workflowId!);
        const active = builderRef.current;
        if (active.sessionId !== snapshot.id) return;
        if (flow.deployedAgentId) active.setDeployedAgentId(flow.deployedAgentId);
        if (flow.runUrl) active.setRunUrl(flow.runUrl);
        if (flow.name) {
          const chat = listChatHistory().find((item) => item.id === snapshot.id);
          if (!chat?.titleLocked) active.setName(flow.name);
        }
        if (!active.nodes.length && flow.nodes.length) {
          active.applyGraphEvent("graph-relayout", { nodes: flow.nodes });
          if (active.phase === "awaiting approval") {
            active.setPhase("building");
          }
        }
        if ((!Array.isArray(snapshot.edges) || !snapshot.edges.length) && flow.edges.length) {
          active.applyGraphEvent("edges-sync", {
            edges: flow.edges,
            nodes: flow.nodes.length ? flow.nodes : active.nodes,
          });
        }
      } catch {
        // Session document already painted the graph.
      }
    })();
  }

  reconcileSessionHitl(activeBuilder, snapshot.id, snapshot);

  const hasPendingHitl = Boolean(
    activeBuilder.pendingUserQuestion ||
    activeBuilder.pendingComposioExecQueue.length ||
    activeBuilder.pendingRuntimeInput ||
    intentApprovalPark ||
    (snapshot.status === "awaiting_approval" &&
      snapshot.plan &&
      activeBuilder.nodes.length === 0) ||
    (snapshot.status === "awaiting_intent_approval" && snapshot.intent && !keepApprovedIntent),
  );
  setChatAwaitingInput(snapshot.id, hasPendingHitl);

  const phase = String(snapshot.status ?? "");
  // Multiplex: sidebar may still say Working… after a soft park / tab switch even
  // when GET briefly reports isRunning=false — keep busy (FlowOps never leaves the page).
  // isChatBuilding is a busy mirror — never feed it back into busy (Deploy lock latch).
  const signalLive =
    phase !== "complete" && (isChatStreaming(snapshot.id) || isWorkflowStreamActive(snapshot.id));
  const shouldResume =
    phase !== "complete" &&
    !hasPendingHitl &&
    !runActive &&
    !signalLive &&
    shouldAutoResumeAfterRestore(phase, hasPendingHitl);

  // FlowOps applyBuildSessionSnapshot: isGenerating false, then re-arm only for
  // a live run or orphan mid-build resume. Intentional Stop leaves buildStalled.
  // Complete: always idle so Deploy/Run unlock after reopen (ignore stale live flags).
  if (phase === "complete") {
    activeBuilder.setBusy(false);
    setChatLive(snapshot.id, false);
    setChatStreaming(snapshot.id, false);
    setChatBuilding(snapshot.id, false);
  } else if (hasPendingHitl) {
    activeBuilder.setBusy(false);
    setChatLive(snapshot.id, false);
  } else if (shouldResume || (!activeBuilder.buildStalled && (runActive || signalLive))) {
    activeBuilder.setBusy(true);
    activeBuilder.setBuildStalled(false);
    setChatLive(snapshot.id, true);
    if (shouldResume) resumeBuildAfterRestore(snapshot.id);
  } else {
    activeBuilder.setBusy(false);
    setChatLive(snapshot.id, false);
    setChatStreaming(snapshot.id, false);
    setChatBuilding(snapshot.id, false);
    if (phase === "building" && !runActive && !signalLive) {
      activeBuilder.setBuildStalled(true);
    }
  }

  emitChatLoaded({ sessionId: snapshot.id });
}

/** @deprecated Use applySessionRestore */
export const applyLiveSessionSnapshot = applySessionRestore;

/** Last-resort paint from local crash buffer when GET fails. */
export async function restoreFromLocalBuffer(options: {
  sessionId: string;
  builderRef: BuilderRef;
  normalizedPrompt?: string;
  emitLoaded?: boolean;
}): Promise<boolean> {
  const sessionId = options.sessionId.trim();
  if (!sessionId) return false;
  const messages = await awaitPersistedLiveBuffer(sessionId);
  if (!messages.length) return false;

  persistLastFlowBuilderSessionId(sessionId);
  const firstUser = messages.find((message) => message.type === "user" && message.text.trim());
  const promptText = firstUser?.text.trim() || options.normalizedPrompt?.trim() || null;
  const streamActive = isChatStreaming(sessionId) || isWorkflowStreamActive(sessionId);
  const active = options.builderRef.current;

  active.hydrateChatSession({
    sessionId,
    messages,
    sourcePrompt: promptText,
    phase: "planning",
  });
  active.setPendingUserQuestion(null);
  active.clearPendingComposioExec();
  active.setPendingRuntimeInput(null);
  setChatAwaitingInput(sessionId, false);
  active.setError(null);
  if (streamActive) {
    active.setBusy(true);
    setChatLive(sessionId, true);
  } else {
    active.setBusy(false);
    setChatLive(sessionId, false);
  }
  syncFlowBuilderSessionUrl(sessionId);
  setFocusedSession(sessionId);
  if (options.emitLoaded !== false) emitChatLoaded({ sessionId });
  // Local buffer has transcript only — re-fetch parked MCQ/Composio forms.
  void ensureSessionHitlFromServer(options.builderRef, sessionId);
  return true;
}

/** @deprecated Use restoreFromLocalBuffer */
export async function restoreLocalChat(options: {
  sessionId: string;
  builderRef: BuilderRef;
  normalizedPrompt: string;
  cancelled: () => boolean;
  emitLoaded?: boolean;
}): Promise<boolean> {
  if (options.cancelled()) return false;
  return restoreFromLocalBuffer(options);
}

/** Test helper — paint rules without a full builder. */
export function paintRestoreMessages(input: {
  serverMessages: BuilderMessage[];
  liveBuffer: BuilderMessage[];
  seedPrompt: string;
  sessionId: string;
  streamActive?: boolean;
}): BuilderMessage[] {
  const server = cleanTranscript(input.serverMessages);
  const live = cleanTranscript(input.liveBuffer);
  if (input.streamActive && live.length > 0) return live;
  if (live.length > 0 && hasUserTurn(live)) {
    if (!hasUserTurn(server) || transcriptWeight(live) >= transcriptWeight(server)) {
      return live;
    }
    return server;
  }
  if (server.length > 0) return server;
  if (live.length > 0) return live;
  return seedUser(input.sessionId, input.seedPrompt);
}

/** @deprecated */
export const hydrateTranscript = paintRestoreMessages;
